<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    width="800px"
    custom-class="MyDialog cross-entity-supervision-dialog"
    @close="handleClose"
  >
    <!-- 表单内容 -->
    <div class="dialog-content">
      <!-- 引导语 -->
      <div class="guide-text" v-if="isCreateMode">
        <p>{{ $t('serviceCenter.crossEntityDialogGuideLine1') }}</p>
        <p>{{ $t('serviceCenter.crossEntityDialogGuideLine2') }}</p>
      </div>

      <el-form
        ref="supervisionForm"
        :model="formData"
        :rules="formRules"
        label-width="150px"
        class="supervision-form"
      >
        <!-- 学员账号 -->
        <el-form-item :label="$t('serviceCenter.crossEntityStudentAccount') + '：'" prop="studentAccount">
          <el-input
            v-if="!isViewMode"
            v-model="formData.studentAccount"
            :placeholder="$t('serviceCenter.crossEntityDialogStudentAccountPlaceholder')"
            :disabled="preFilledAccount"
            @blur="validateStudentAccount"
          />
          <span v-else class="readonly-text">{{ formData.studentAccount }}</span>
        </el-form-item>

        <!-- 学员信息 -->
        <template>
          <el-form-item :label="$t('serviceCenter.crossEntityStudentName') + '：'">
            <span class="readonly-text">{{ studentInfo.studentName || '-' }}</span>
          </el-form-item>
          <el-form-item :label="$t('serviceCenter.crossEntityDealerId') + '：'">
            <span class="readonly-text">{{ studentInfo.agentId || '-' }}</span>
          </el-form-item>
          <el-form-item :label="$t('serviceCenter.crossEntityDealerName') + '：'">
            <span class="readonly-text">{{ studentInfo.agentName || '-' }}</span>
          </el-form-item>
        </template>
        
        <!-- 转入服务中心信息 -->
        <el-form-item :label="$t('serviceCenter.crossEntityDialogTransferServiceCenterId') + '：'">
          <span class="readonly-text">{{ studentInfo.campusId || '-' }}</span>
        </el-form-item>
        <el-form-item :label="$t('serviceCenter.crossEntityDialogTransferServiceCenterName') + '：'">
          <span class="readonly-text">{{ studentInfo.campusName || '-' }}</span>
        </el-form-item>

        <!-- 情况说明 -->
        <el-form-item :label="$t('serviceCenter.crossEntityDialogSituationDescription') + '：'" prop="description">
          <el-input
            v-if="!isViewMode"
            v-model="formData.description"
            type="textarea"
            :rows="4"
            :placeholder="$t('serviceCenter.crossEntityDialogSituationPlaceholder')"
            maxlength="200"
            show-word-limit
          />
          <div v-else class="readonly-textarea">{{ formData.description }}</div>
        </el-form-item>

        <!-- 证明文件 -->
        <el-form-item :label="$t('serviceCenter.crossEntityDialogProofDocuments') + '：'" prop="evidenceFiles" :required="!isViewMode">
          <div class="evidence-files-section">
            <div class="upload-guide" v-if="!isViewMode">
              <span>{{ $t('serviceCenter.crossEntityDialogUploadProofHint') }}</span>
            </div>

            <!-- 文件列表（上传按钮与图片并排排列） -->
            <div class="file-list-wrap">
              <div
                v-for="(file, index) in formData.evidenceFiles"
                :key="index"
                class="file-item"
              >
                <img :src="file.url" :alt="$t('serviceCenter.crossEntityDialogProofImageAlt')" />
                <div class="file-actions">
                  <i class="el-icon-view" @click="previewImage(file.url)" :title="$t('app.preview')"></i>
                  <i
                    v-if="!isViewMode"
                    class="el-icon-delete"
                    @click="removeFile(index)"
                    :title="$t('app.delete')"
                  ></i>
                </div>
              </div>

              <!-- 上传按钮与图片同一行展示 -->
              <div v-if="!isViewMode && formData.evidenceFiles.length < 9" class="upload-area">
                <el-upload
                  :action="uploadUrl"
                  :headers="uploadHeaders"
                  :show-file-list="false"
                  :on-success="handleUploadSuccess"
                  :before-upload="beforeUpload"
                  accept="image/jpeg,image/jpg,image/png,image/bmp,image/gif"
                >
                  <div class="upload-trigger">
                    <i class="el-icon-plus"></i>
                    <span>{{ $t('serviceCenter.crossEntityDialogClickUploadImage') }}</span>
                  </div>
                </el-upload>
              </div>
            </div>
          </div>
        </el-form-item>

        <!-- 审核信息（仅审核和查看模式显示） -->
        <template v-if="isAuditMode || (isViewMode && formData.auditStatus !== '' && formData.auditStatus !== null && formData.auditStatus !== undefined)">
          <el-form-item :label="$t('serviceCenter.crossEntityDialogAuditResult') + '：'" prop="auditStatus" v-if="isAuditMode">
            <el-select v-model="formData.auditStatus" :placeholder="$t('serviceCenter.crossEntityDialogSelectAuditResult')">
              <el-option :label="$t('serviceCenter.crossEntityDialogAuditOptionApprove')" :value="1"></el-option>
              <el-option :label="$t('serviceCenter.crossEntityDialogAuditOptionReject')" :value="2"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item :label="$t('serviceCenter.crossEntityDialogAuditStatus') + '：'" v-else-if="isViewMode">
            <span class="readonly-text">{{ getAuditStatusText(formData.auditStatus) }}</span>
          </el-form-item>

          <el-form-item :label="$t('serviceCenter.auditOpinion') + '：'" prop="auditRemark" :required="isAuditMode">
            <el-input
              v-if="isAuditMode"
              v-model="formData.auditRemark"
              type="textarea"
              :rows="3"
              :placeholder="$t('serviceCenter.crossEntityDialogAuditRemarkPlaceholder')"
              maxlength="200"
              show-word-limit
            />
            <div v-else-if="isViewMode" class="readonly-textarea">{{ formData.auditRemark || '-' }}</div>
          </el-form-item>

          <el-form-item :label="$t('serviceCenter.crossEntityAuditTime') + '：'" v-if="isViewMode && formData.auditTime">
            <span class="readonly-text">{{ formData.auditTime || '-' }}</span>
          </el-form-item>

          <el-form-item :label="$t('serviceCenter.crossEntityAuditor') + '：'" v-if="isViewMode && formData.auditorName">
            <span class="readonly-text">{{ formData.auditorName || '-' }}</span>
          </el-form-item>
        </template>
      </el-form>
    </div>

    <!-- 底部按钮 -->
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">{{ isViewMode ? $t('serviceCenter.crossEntityDialogBtnClose') : $t('serviceCenter.crossEntityDialogBtnCancel') }}</el-button>
      <el-button
        v-if="!isViewMode"
        type="primary"
        @click="handleSubmit"
        :loading="submitLoading"
        :disabled="isCreateMode && !isAccountValid"
      >
        {{ isAuditMode ? $t('serviceCenter.crossEntityDialogBtnSubmitAudit') : $t('serviceCenter.crossEntityDialogBtnSubmitApply') }}
      </el-button>
    </div>

    <!-- 图片预览弹框 -->
    <el-dialog :visible.sync="previewVisible" width="800px" custom-class="image-preview-dialog" append-to-body>
      <img :src="previewImageUrl" style="width: 100%"/>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { getBaseUrl } from "@/util/config";
import LoginUserHelper from "@/util/LoginUserHelper";
import {
  auditCrossEntitySupervisionApplication,
  checkDuxueApplyAccountInfo,
  checkDuxueApply,
  submitDuxueApply
} from "@/api/CrossEntitySupervisionApi";

export default {
  name: "CrossEntitySupervisionDialog",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: "create", // create: 新增, view: 查看, audit: 审核
      validator: value => ["create", "view", "audit"].includes(value)
    },
    // 预填充的学员账号（从转服务中心触发时传入）
    preFilledStudentAccount: {
      type: String,
      default: ""
    },
    // 申请数据（查看/审核模式时传入）
    applicationData: {
      type: Object,
      default: () => ({})
    },
    // 当前经销商信息
    currentDealerInfo: {
      type: Object,
      default: () => ({
        dealerId: "",
        dealerName: ""
      })
    },
    // 申请来源：0 自主创建 1 学员申请转入 2 邀请学员
    applySource: {
      type: String,
      default: "0",
      validator: value => ["0", "1", "2"].includes(value)
    }
  },
  data() {
    return {
      dialogVisible: false,
      submitLoading: false,
      formData: {
        studentAccount: "",
        description: "",
        evidenceFiles: [],
        auditStatus: "",
        auditRemark: ""
      },
      studentInfo: {
        studentName: "",
        campusId: "",
        campusName: "",
        agentId: "",
        agentName: ""
      },
      previewVisible: false,
      previewImageUrl: "",
      uploadUrl: `${getBaseUrl()}base/upload/uploadPicture`,
      uploadHeaders: {
        authorization: LoginUserHelper.getUserToken()
      },
      isAccountValid: false
    };
  },
  computed: {
    dialogTitle() {
      const titles = {
        create: this.$t('serviceCenter.crossEntityDialogTitleCreate'),
        view: this.$t('serviceCenter.crossEntityDialogTitleView'),
        audit: this.$t('serviceCenter.crossEntityDialogTitleAudit')
      };
      return titles[this.mode] || this.$t('serviceCenter.crossEntityDialogTitleFallback');
    },
    formRules() {
      return {
        studentAccount: [
          {
            required: true,
            message: this.$t('serviceCenter.crossEntityDialogRuleStudentAccount'),
            trigger: 'blur'
          }
        ],
        description: [
          {
            required: true,
            message: this.$t('serviceCenter.crossEntityDialogRuleDescriptionRequired'),
            trigger: 'blur'
          },
          {
            min: 5,
            max: 200,
            message: this.$t('serviceCenter.crossEntityDialogRuleDescriptionLength'),
            trigger: 'blur'
          }
        ],
        evidenceFiles: [
          {
            validator: this.validateEvidenceFiles,
            message: this.$t('serviceCenter.crossEntityDialogRuleEvidence'),
            trigger: 'change'
          }
        ],
        auditStatus: [
          {
            required: true,
            message: this.$t('serviceCenter.crossEntityDialogRuleAuditResult'),
            trigger: 'change'
          }
        ],
        auditRemark: [
          {
            required: true,
            message: this.$t('serviceCenter.crossEntityDialogRuleAuditRemark'),
            trigger: 'blur'
          }
        ]
      };
    },
    isViewMode() {
      return this.mode === "view";
    },
    isAuditMode() {
      return this.mode === "audit";
    },
    isCreateMode() {
      return this.mode === "create";
    },
    preFilledAccount() {
      return !!this.preFilledStudentAccount;
    }
  },
  watch: {
    visible: {
      handler(val) {
        this.dialogVisible = val;
        if (val) {
          this.initDialog();
        }
      },
      immediate: true
    },
    dialogVisible(val) {
      if (!val) {
        this.$emit("update:visible", false);
      }
    }
  },
  methods: {
    // 初始化弹框
    initDialog() {
      this.resetForm();
      
      if (this.preFilledStudentAccount) {
        this.formData.studentAccount = this.preFilledStudentAccount;
        this.validateStudentAccount();
      }
      
      if ((this.isViewMode || this.isAuditMode) && this.applicationData) {
        this.loadApplicationData();
      }
    },

    // 重置表单
    resetForm() {
      this.formData = {
        studentAccount: "",
        description: "",
        evidenceFiles: [],
        auditStatus: "",
        auditRemark: ""
      };
      this.studentInfo = {
        studentName: "",
        campusId: "",
        campusName: "",
        agentId: "",
        agentName: ""
      };
      this.$nextTick(() => {
        this.$refs.supervisionForm && this.$refs.supervisionForm.clearValidate();
      });
    },

    // 加载申请数据（查看/审核模式）
    loadApplicationData() {
      this.formData = { ...this.applicationData };
      this.studentInfo = {
        studentName: this.applicationData.studentName || "",
        campusId: this.applicationData.campusId || "",
        campusName: this.applicationData.campusName || "",
        agentId: this.applicationData.agentId || "",
        agentName: this.applicationData.agentName || ""
      };
    },

    // 验证学员账号（servicepoint 校验）
    async validateStudentAccount() {
      if (!this.formData.studentAccount.trim()) {
        this.studentInfo = {
          studentName: "",
          campusId: "",
          campusName: "",
          agentId: "",
          agentName: ""
        };
        this.isAccountValid = false;
        return;
      }

      try {
        // 账号校验，获取账户/经销商信息
        const infoRes = await checkDuxueApplyAccountInfo({
          account: this.formData.studentAccount
        });
        if (infoRes.code !== 0) {
          this.$message.error(this.$getCodeMessage(infoRes.code, infoRes.message, infoRes.messageArgs) || this.$t('app.auto_85_7d350f62'));
          return;
        }
        const info = infoRes.data || {};
        // 预设置展示信息（使用返回的经销商信息填充设备经销商）
        this.studentInfo = {
          studentName: info.trueName || info.account || "",
          campusId: info.campusId || "",
          campusName: info.campusName || "",
          agentId: info.agentId || "",
          agentName: info.agentName || ""
        };
        this.isAccountValid = true;

        // 仅校验账号有效性与设备经销商信息，不做申请前置校验
      } catch (error) {
        console.error("验证学员账号失败:", error);
        this.$message.error(this.$t('app.auto_86_492ffaa8'));
        this.isAccountValid = false;
      }
    },

    // 文件上传前验证
    beforeUpload(file) {
      const isImage = /^image\/(jpeg|jpg|png|bmp|gif)$/.test(file.type);
      const isLt5M = file.size / 1024 / 1024 < 5;

      if (!isImage) {
        this.$message.error(this.$t('app.auto_87_4591b39a'));
        return false;
      }
      if (!isLt5M) {
        this.$message.error(this.$t('app.auto_88_6d73238a'));
        return false;
      }
      return true;
    },

    // 文件上传成功
    handleUploadSuccess(response) {
      if (response.code === 0) {
        this.formData.evidenceFiles.push({
          name: response.data.fileName,
          url: response.data.uploadPath
        });
        this.$message.success(this.$t('app.auto_89_7953c2d5'));
        this.$refs.supervisionForm.validateField("evidenceFiles");
      } else {
        this.$message.error(this.$getCodeMessage(response.code, response.message, response.messageArgs) || this.$t('app.auto_90_33e94845'));
      }
    },

    // 删除文件
    removeFile(index) {
      this.formData.evidenceFiles.splice(index, 1);
      this.$refs.supervisionForm.validateField("evidenceFiles");
    },

    // 预览图片
    previewImage(url) {
      this.previewImageUrl = url;
      this.previewVisible = true;
    },

    // 验证证明文件
    validateEvidenceFiles(rule, value, callback) {
      if (!this.isViewMode && this.formData.evidenceFiles.length === 0) {
        callback(new Error(this.$t('serviceCenter.crossEntityDialogRuleEvidence')));
      } else {
        callback();
      }
    },

    // 获取审核状态文本
    getAuditStatusText(status) {
      const statusMap = {
        0: this.$t('serviceCenter.crossEntityApplyStatusPending'),
        1: this.$t('serviceCenter.crossEntityDialogAuditOptionApprove'),
        2: this.$t('serviceCenter.crossEntityDialogAuditOptionReject'),
        pending: this.$t('serviceCenter.crossEntityApplyStatusPending'),
        approved: this.$t('serviceCenter.crossEntityDialogAuditOptionApprove'),
        rejected: this.$t('serviceCenter.crossEntityDialogAuditOptionReject')
      };
      return statusMap[status] || status;
    },

    // 提交表单
    handleSubmit() {
      this.$refs.supervisionForm.validate(async (valid) => {
        if (!valid) {
          return false;
        }

        this.submitLoading = true;
        try {
          let res;
          
          if (this.isAuditMode) {
            // 审核申请
            res = await auditCrossEntitySupervisionApplication({
              applicationId: this.applicationData.id,
              auditStatus: this.formData.auditStatus,
              auditRemark: this.formData.auditRemark
            });
          } else {
            // 新增申请（servicepoint 提交）
            // 先做一次前置校验，保障提示及时
            const preCheck = await checkDuxueApply({ account: this.formData.studentAccount });
            if (preCheck.code !== 0) {
              this.$message.warning(this.$getCodeMessage(preCheck.code, preCheck.message, preCheck.messageArgs) || this.$t('app.auto_94_15733a82'));
              this.submitLoading = false;
              return;
            }
            const preData = preCheck.data || {};
            const crossType = Number(preData.duxueCrossType);
            // 仅当 duxueCrossType 为 3、4 时允许提交，其余情况提示并拦截
            if (crossType !== 3 && crossType !== 4) {
              const tipMap = {
                0: this.$t('app.auto_95_294563f7'),
                1: this.$t('app.auto_96_0d7118c4'),
                2: this.$t('app.auto_97_daafa233'),
                5: this.$t('app.auto_98_ed78a387')
              };
              this.$message.warning(tipMap[crossType] || this.$t('app.auto_99_d0373366'));
              this.submitLoading = false;
              return;
            }
            res = await submitDuxueApply({
              account: this.formData.studentAccount,
              applyContent: this.formData.description,
              applyPics: (this.formData.evidenceFiles || []).map(f => f.url),
              applySource: this.applySource
            });
          }

          if (res.code === 0) {
            this.$message.success(
              this.isAuditMode ? this.$t('app.auto_100_b3dad3de') : this.$t('app.auto_101_34344d00')
            );
            this.$emit("success", res.data);
            this.handleClose();
          } else {
            this.$message.error(this.$getCodeMessage(res.code, res.message, res.messageArgs) || this.$t('app.auto_102_d12681fa'));
          }
        } catch (error) {
          console.error("提交失败:", error);
          this.$message.error(this.$t('app.auto_103_3a39ddcd'));
        } finally {
          this.submitLoading = false;
        }
      });
    },

    // 关闭弹框
    handleClose() {
      this.dialogVisible = false;
      this.$emit("close");
    }
  }
};
</script>

<style lang="scss" scoped>
  :deep(.cross-entity-supervision-dialog .el-dialog__body) {
    max-height: 60vh;
    overflow-y: auto;
    padding-right: 12px;
  }
.cross-entity-supervision-dialog {
  /* 限制弹框最大高度，超出滚动 */
  

  .dialog-content {
    .guide-text {
      background: #f5f7fa;
      padding: 15px;
      border-radius: 4px;
      margin-bottom: 20px;
      
      p {
        margin: 0 0 5px 0;
        color: #606266;
        line-height: 1.5;
        
        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    .supervision-form {
      .readonly-text {
        color: #606266;
      }

      .readonly-textarea {
        color: #606266;
        line-height: 1.5;
        min-height: 60px;
        padding: 8px 0;
      }

      .evidence-files-section {
        .upload-guide {
          color: #909399;
          font-size: 14px;
          margin-bottom: 10px;
        }

        .upload-area {
          margin-bottom: 15px;

          .upload-trigger {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            width: 148px;
            height: 148px;
            border: 1px dashed #c0ccda;
            border-radius: 4px;
            cursor: pointer;
            transition: border-color 0.3s;

            &:hover {
              border-color: #409eff;
            }

            i {
              font-size: 24px;
              color: #c0ccda;
              margin-bottom: 8px;
            }

            span {
              color: #606266;
              font-size: 14px;
            }
          }
        }

        /* 文件列表容器，上传按钮与图片并排，自动换行 */
        .file-list-wrap {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;

          .file-item {
            position: relative;
            width: 148px;
            height: 148px;
            border: 1px solid #d9d9d9;
            border-radius: 4px;
            overflow: hidden;

            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }

            .file-actions {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(0, 0, 0, 0.5);
              display: none;
              align-items: center;
              justify-content: center;
              gap: 15px;

              i {
                color: #fff;
                font-size: 18px;
                cursor: pointer;
                transition: transform 0.2s;

                &:hover {
                  transform: scale(1.2);
                }
              }
            }

            &:hover .file-actions {
              display: flex;
            }
          }

          /* 上传触发块与图片同规格，避免换行 */
          .upload-area {
            .upload-trigger {
              width: 148px;
              height: 148px;
            }
          }
        }
      }
    }
  }

  .dialog-footer {
    text-align: right;
  }
}

.image-preview-dialog {
  :deep(.el-dialog__body) {
    text-align: center;
    padding: 20px;
  }
}
::v-deep .image-preview-dialog {
  margin: 15vh 0 50px !important;
  top: 0 !important;
  left: 25% !important;
  transform: none !important;
}
</style>
